import React, { useMemo } from "react";
import { Dialog } from "primereact/dialog";
import { Chart } from "primereact/chart";
import type { WeeklyStationMetric } from "../../../types/kpiDashboard";

interface Props {
  visible: boolean;
  onHide: () => void;
  trend: WeeklyStationMetric[];
}

const AC_COLOR  = "#4f83cc";
const DC_COLOR  = "#e05b4b";
const AVG_COLOR = "#22a355";

const EnergyDetailDialog: React.FC<Props> = ({ visible, onHide, trend }) => {
  const chartData = useMemo(() => ({
    labels: trend.map((d) => d.period),
    datasets: [
      {
        type: "bar" as const,
        label: "AC (kWh)",
        data: trend.map((d) => d.energyAc),
        backgroundColor: AC_COLOR,
        borderRadius: 3,
        stack: "energy",
        yAxisID: "y",
      },
      {
        type: "bar" as const,
        label: "DC (kWh)",
        data: trend.map((d) => d.energyDc),
        backgroundColor: DC_COLOR,
        borderRadius: 3,
        stack: "energy",
        yAxisID: "y",
      },
      {
        type: "line" as const,
        label: "Daily Avg (sessions)",
        data: trend.map((d) => d.dailySessions),
        borderColor: AVG_COLOR,
        backgroundColor: "transparent",
        borderWidth: 2.5,
        pointRadius: 4,
        pointBackgroundColor: AVG_COLOR,
        tension: 0.35,
        yAxisID: "y2",
      },
    ],
  }), [trend]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index" as const, intersect: false },
    animation: { duration: 300 },
    plugins: {
      legend: {
        position: "top" as const,
        labels: { color: "#495057", font: { size: 13 }, padding: 16 },
      },
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const val = ctx.parsed.y;
            if (ctx.dataset.label?.includes("Daily")) return ` Daily Avg: ${val} sessions`;
            return ` ${ctx.dataset.label}: ${val.toLocaleString()} kWh`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        ticks: { color: "#6c757d", font: { size: 11 } },
        grid: { color: "#f3f4f6" },
      },
      y: {
        stacked: true,
        position: "left" as const,
        ticks: {
          color: "#6c757d",
          callback: (v: number) => `${(v / 1000).toFixed(0)}k`,
        },
        grid: { color: "#f3f4f6" },
        title: { display: true, text: "Energy (kWh)", color: "#6c757d" },
      },
      y2: {
        position: "right" as const,
        grid: { drawOnChartArea: false },
        ticks: { color: AVG_COLOR, font: { size: 11 } },
        title: { display: true, text: "Daily Avg (sessions)", color: AVG_COLOR },
      },
    },
  }), []);

  return (
    <Dialog
      header="Energy Consumption (kWh)"
      visible={visible}
      onHide={onHide}
      style={{ width: "90vw", maxWidth: "1100px" }}
      modal
      dismissableMask
    >
      <Chart
        type="bar"
        data={chartData}
        options={chartOptions}
        style={{ height: "460px" }}
      />
    </Dialog>
  );
};

export default EnergyDetailDialog;
